import { useState, useEffect } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { apiFetch, useAuth } from "../App";

const PLANS = [
  {
    key: "edge",
    name: "Edge",
    monthlyKey: "edge_monthly",
    annualKey: "edge_annual",
    monthlyPrice: 99,
    annualPrice: 890,
    annualMonthly: 74,
    color: "var(--accent)",
    highlight: true,
    features: [
      "Everything in Free",
      "Real-time data across 6 platforms",
      "Advanced statistical models",
      "AI-powered Smart Parlay builder",
      "Automated Telegram alerts",
      "Market movement tracking",
      "Historical data access",
      "Priority support",
    ],
  },
  {
    key: "elite",
    name: "Elite",
    monthlyKey: "elite_monthly",
    annualKey: "elite_annual",
    monthlyPrice: 199,
    annualPrice: 1790,
    annualMonthly: 149,
    color: "#f59e0b",
    highlight: false,
    features: [
      "Everything in Edge",
      "Poisson statistical modeling",
      "Team performance analysis",
      "Portfolio optimization tools",
      "Custom alert configuration",
      "API access",
      "Dedicated support",
      "Early access to new features",
    ],
  },
];

function PlanCard({ plan, billing, onSelect, selected, currentPlan }) {
  const price = billing === "monthly" ? plan.monthlyPrice : plan.annualPrice;
  const planKey = billing === "monthly" ? plan.monthlyKey : plan.annualKey;
  const isCurrent = currentPlan === plan.key;

  return (
    <div style={{
      background: plan.highlight ? "rgba(0,229,176,0.04)" : "var(--bg2)",
      border: `1px solid ${selected === planKey ? plan.color : plan.highlight ? "rgba(0,229,176,0.2)" : "var(--border)"}`,
      borderRadius: "var(--radius)",
      padding: "24px",
      position: "relative",
      cursor: isCurrent ? "default" : "pointer",
      transition: "all .15s",
    }} onClick={() => !isCurrent && onSelect(planKey)}>

      {plan.highlight && (
        <div style={{ position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)", background: "var(--accent)", color: "#080d17", fontSize: 11, fontWeight: 700, padding: "4px 16px", borderRadius: 20, whiteSpace: "nowrap" }}>
          MOST POPULAR
        </div>
      )}

      {isCurrent && (
        <div style={{ position: "absolute", top: -12, right: 16, background: "var(--bg3)", color: "var(--text3)", fontSize: 11, fontWeight: 700, padding: "4px 16px", borderRadius: 20, border: "1px solid var(--border)" }}>
          CURRENT PLAN
        </div>
      )}

      <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 22, marginBottom: 4 }}>{plan.name}</div>

      <div style={{ marginBottom: 20 }}>
        <span style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 36, color: plan.color }}>P{price}</span>
        <span style={{ fontSize: 14, color: "var(--text3)" }}>/{billing === "monthly" ? "month" : "year"}</span>
        {billing === "annual" && (
          <div style={{ fontSize: 12, color: "var(--success)", marginTop: 4 }}>
            Save 25% — P{plan.annualMonthly}/month
          </div>
        )}
      </div>

      <ul style={{ listStyle: "none", padding: 0, margin: "0 0 24px", display: "flex", flexDirection: "column", gap: 8 }}>
        {plan.features.map(f => (
          <li key={f} style={{ fontSize: 13, color: "var(--text2)", display: "flex", gap: 8 }}>
            <span style={{ color: plan.color, flexShrink: 0 }}>✓</span> {f}
          </li>
        ))}
      </ul>

      {!isCurrent && (
        <div style={{
          width: "100%", padding: "12px", borderRadius: "var(--radius-sm)", textAlign: "center",
          background: selected === planKey ? plan.color : "transparent",
          border: `1px solid ${selected === planKey ? plan.color : "var(--border)"}`,
          color: selected === planKey ? "#080d17" : "var(--text2)",
          fontWeight: 700, fontSize: 14, cursor: "pointer",
        }}>
          {selected === planKey ? "✓ Selected" : `Get ${plan.name}`}
        </div>
      )}
    </div>
  );
}

export default function Upgrade() {
  const { user } = useAuth();
  const [billing, setBilling] = useState("monthly");
  const [selectedPlan, setSelectedPlan] = useState("edge_monthly");
  const [step, setStep] = useState("select"); // select, checkout, success
  const [paymentError, setPaymentError] = useState("");
  const [successData, setSuccessData] = useState(null);
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  useEffect(() => {
    // Update selected plan when billing changes
    const planBase = selectedPlan.replace("_monthly", "").replace("_annual", "");
    setSelectedPlan(`${planBase}_${billing}`);
  }, [billing]);

  const isMobile = width < 768;
  const selectedPlanDetails = PLANS.find(p => p.monthlyKey === selectedPlan || p.annualKey === selectedPlan);
  const selectedPrice = billing === "monthly" ? selectedPlanDetails?.monthlyPrice : selectedPlanDetails?.annualPrice;

  async function createOrder() {
    try {
      const data = await apiFetch("/payments/create-order", {
        method: "POST",
        body: JSON.stringify({ planKey: selectedPlan }),
      });
      return data.orderId;
    } catch (err) {
      setPaymentError("Failed to create payment. Please try again.");
      throw err;
    }
  }

  async function onApprove(data) {
    try {
      setPaymentError("");
      const result = await apiFetch("/payments/capture-order", {
        method: "POST",
        body: JSON.stringify({ orderId: data.orderID, planKey: selectedPlan }),
      });
      setSuccessData(result);
      setStep("success");
    } catch (err) {
      setPaymentError("Payment capture failed. Please contact support@oddsedgebw.co.bw");
    }
  }

  function onError(err) {
    setPaymentError("Payment failed. Please try again or contact support.");
    console.error("PayPal error:", err);
  }

    console.log('PayPal Client ID:', import.meta.env.VITE_PAYPAL_CLIENT_ID);

  return (
    <PayPalScriptProvider options={{
      clientId: import.meta.env.VITE_PAYPAL_CLIENT_ID,
      currency: "USD",
      intent: "capture",
    }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

        {/* Header */}
        <div style={{
          background: "rgba(0,229,176,0.04)", border: "1px solid rgba(0,229,176,0.15)",
          borderRadius: "var(--radius)", padding: "16px 20px",
        }}>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 16, marginBottom: 4 }}>
            Upgrade Your Plan
          </div>
          <div style={{ fontSize: 13, color: "var(--text3)" }}>
            Unlock the full power of OddsEdgeBW. Cancel anytime.
          </div>
        </div>

        {/* Success State */}
        {step === "success" && successData && (
          <div className="card" style={{ padding: "40px 20px", textAlign: "center" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 22, marginBottom: 8, color: "var(--accent)" }}>
              Payment Successful!
            </div>
            <div style={{ fontSize: 15, color: "var(--text2)", marginBottom: 24 }}>
              Your {successData.plan} plan is now active until{" "}
              {new Date(successData.expiresAt).toLocaleDateString("en-BW", { day: "numeric", month: "long", year: "numeric" })}.
            </div>
            <div style={{ padding: "12px 20px", background: "rgba(0,229,176,0.08)", border: "1px solid rgba(0,229,176,0.2)", borderRadius: "var(--radius-sm)", fontSize: 13, color: "var(--text3)", marginBottom: 24 }}>
              A confirmation email has been sent to {user?.email}
            </div>
            <button
              className="btn btn-primary"
              onClick={() => window.location.reload()}
              style={{ padding: "12px 32px" }}
            >
              Start Using {successData.plan.charAt(0).toUpperCase() + successData.plan.slice(1)} →
            </button>
          </div>
        )}

        {step !== "success" && (
          <>
            {/* Billing toggle */}
            <div style={{ display: "flex", justifyContent: "center" }}>
              <div style={{ display: "flex", background: "var(--bg2)", borderRadius: "var(--radius-sm)", padding: 4 }}>
                <button
                  onClick={() => setBilling("monthly")}
                  style={{
                    padding: "8px 24px", fontSize: 13, fontWeight: 600,
                    borderRadius: "var(--radius-sm)", border: "none", cursor: "pointer",
                    background: billing === "monthly" ? "var(--accent)" : "transparent",
                    color: billing === "monthly" ? "#080d17" : "var(--text3)",
                  }}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBilling("annual")}
                  style={{
                    padding: "8px 24px", fontSize: 13, fontWeight: 600,
                    borderRadius: "var(--radius-sm)", border: "none", cursor: "pointer",
                    background: billing === "annual" ? "var(--accent)" : "transparent",
                    color: billing === "annual" ? "#080d17" : "var(--text3)",
                  }}
                >
                  Annual <span style={{ fontSize: 10, color: billing === "annual" ? "#080d17" : "var(--accent)" }}>Save 25%</span>
                </button>
              </div>
            </div>

            {/* Plan cards */}
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16 }}>
              {PLANS.map(plan => (
                <PlanCard
                  key={plan.key}
                  plan={plan}
                  billing={billing}
                  onSelect={setSelectedPlan}
                  selected={selectedPlan}
                  currentPlan={user?.plan}
                />
              ))}
            </div>

            {/* Free plan reminder */}
            <div style={{ textAlign: "center", fontSize: 12, color: "var(--text3)" }}>
              Already on Free plan? You can continue using basic features at no cost.
            </div>

            {/* Checkout section */}
            {step === "select" && selectedPlan && user?.plan !== selectedPlanDetails?.key && (
              <div className="card" style={{ padding: "20px" }}>
                <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 13, marginBottom: 16 }}>
                  Complete Your Purchase
                </div>

                {/* Order summary */}
                <div style={{ padding: "14px 16px", background: "var(--bg3)", borderRadius: "var(--radius-sm)", marginBottom: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>
                        {selectedPlanDetails?.name} — {billing === "monthly" ? "Monthly" : "Annual"}
                      </div>
                      <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 2 }}>
                        {billing === "monthly" ? "Renews monthly — we will send you a payment link" : "Valid for 12 months"}
                      </div>
                    </div>
                    <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 20, color: "var(--accent)" }}>
                      P{selectedPrice}
                    </div>
                  </div>
                </div>

                {/* Currency notice */}
                <div style={{ padding: "10px 14px", background: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.15)", borderRadius: "var(--radius-sm)", fontSize: 12, color: "var(--text3)", marginBottom: 16 }}>
                  💱 Payment is processed in USD. Your card will be charged approximately{" "}
                  <strong style={{ color: "var(--warning)" }}>
                    ${billing === "monthly"
                      ? (selectedPrice / 13.5).toFixed(2)
                      : (selectedPrice / 13.5).toFixed(2)
                    } USD
                  </strong>{" "}
                  (P{selectedPrice} BWP at current rate). Exchange rates may vary.
                </div>

                {paymentError && (
                  <div style={{ padding: "10px 14px", background: "rgba(240,71,71,0.08)", borderRadius: "var(--radius-sm)", fontSize: 13, color: "var(--danger)", marginBottom: 16 }}>
                    ⚠️ {paymentError}
                  </div>
                )}

                {/* PayPal Buttons */}
                <PayPalButtons
                  style={{
                    layout: "vertical",
                    color: "gold",
                    shape: "rect",
                    label: "pay",
                    height: 48,
                  }}
                  createOrder={createOrder}
                  onApprove={onApprove}
                  onError={onError}
                  onCancel={() => setPaymentError("Payment cancelled.")}
                />

                <div style={{ fontSize: 11, color: "var(--text3)", textAlign: "center", marginTop: 12 }}>
                  🔒 Secure payment via PayPal · You can pay with debit/credit card without a PayPal account
                </div>
              </div>
            )}

            {/* Already on this plan */}
            {user?.plan === selectedPlanDetails?.key && (
              <div style={{ padding: "14px 16px", background: "rgba(0,229,176,0.06)", border: "1px solid rgba(0,229,176,0.15)", borderRadius: "var(--radius-sm)", fontSize: 13, color: "var(--text2)", textAlign: "center" }}>
                ✅ You are already on the {selectedPlanDetails?.name} plan.
                {user?.planExpiresAt && ` Active until ${new Date(user.planExpiresAt).toLocaleDateString("en-BW", { day: "numeric", month: "long", year: "numeric" })}.`}
              </div>
            )}
          </>
        )}

        {/* Cancellation note */}
        <div style={{ fontSize: 11, color: "var(--text3)", textAlign: "center", lineHeight: 1.6 }}>
          By subscribing you agree to our{" "}
          <a href="/cancellation-policy.html" style={{ color: "var(--text3)", textDecoration: "underline" }}>Cancellation Policy</a>
          {" "}and{" "}
          <a href="/refund-policy.html" style={{ color: "var(--text3)", textDecoration: "underline" }}>Refund Policy</a>.
          Subscriptions renew monthly — we will send you a payment link before renewal.
        </div>
      </div>
    </PayPalScriptProvider>
  );
}